// ExtensionWizard.jsx – Fenster „Erweiterung erstellen“ (vom Dashboard aus)
// Ablauf in vier Schritten:
//   1. Original wählen     – zu welchem bestehenden Stammbaum gehört die Erweiterung?
//   2. Ausgangsperson      – wessen Familie kommt dazu? (z.B. Anias). Zur Auswahl stehen
//                            nur Personen, deren Eltern im Original fehlen.
//   3. Personen übernehmen – vorgeschlagen: Ausgangsperson, Partner, Nachkommen und
//                            deren Partner. Diese erscheinen in der Erweiterung grau.
//   4. Name                – Name der Erweiterung, dann wird sie angelegt.
// Die Daten baut utils/extension.js (buildExtensionData).
import React, { useMemo, useState } from 'react';
import { eligibleAnchors, defaultSelection, buildExtensionData } from '../utils/extension';

const API = 'https://editor.bandeleart.ch';

// Kleines Bild der Person (oder Initialen)
function Avatar({ p }) {
  const initials = (p.name || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return p.photo
    ? <img src={p.photo} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
    : <div style={{ width: 34, height: 34, borderRadius: 6, background: '#ddd', color: '#777', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{initials}</div>;
}

// Props: trees (Liste aus dem Dashboard), token, saveTreeData(treeCode, data) → Promise<bool>,
// onCreated({ code, name }) = Erweiterung öffnen, onClose
export function ExtensionWizard({ trees, token, saveTreeData, onCreated, onClose }) {
  const [step, setStep] = useState('base');       // 'base' | 'anchor' | 'select' | 'name'
  const [baseTree, setBaseTree] = useState(null); // Eintrag aus der Liste
  const [base, setBase] = useState(null);         // geladene Daten des Originals
  const [anchorId, setAnchorId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const personsById = useMemo(() => new Map((base?.persons || []).map(p => [p.id, p])), [base]);
  const anchors = useMemo(() => (base ? eligibleAnchors(base) : []), [base]);

  // 1 → 2: Original laden
  const chooseBase = async (tree) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/api/tree/load`, { method: 'POST', headers: auth, body: JSON.stringify({ treeCode: tree.code }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || 'Der Stammbaum konnte nicht geladen werden.'); return; }
      if (!(d.persons || []).length) { setError('Dieser Stammbaum hat noch keine Personen.'); return; }
      setBaseTree(tree); setBase(d); setStep('anchor');
    } catch { setError('Server nicht erreichbar.'); }
    finally { setBusy(false); }
  };

  // 2 → 3: Ausgangsperson gewählt → Vorschlag übernehmen
  const chooseAnchor = (id) => {
    setAnchorId(id);
    setSelected(defaultSelection(base, id));
    const p = personsById.get(id);
    setName(`Erweiterung: Familie von ${p?.name || 'Unbekannt'}`);
    setStep('select');
  };

  const toggle = (id) => {
    if (id === anchorId) return; // Ausgangsperson muss dabei sein
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  // 4: Erweiterung anlegen, Daten speichern, öffnen
  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Bitte einen Namen eingeben.'); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/api/user/tree/new`, { method: 'POST', headers: auth, body: JSON.stringify({ name: name.trim() }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || 'Fehler beim Erstellen.'); return; }
      const data = buildExtensionData(base, baseTree.code, baseTree.name, baseTree.status, anchorId, [...selected]);
      const ok = await saveTreeData(d.treeCode, data);
      if (!ok) { setError('Die Erweiterung wurde angelegt, die Personen konnten aber nicht gespeichert werden. Bitte nochmals versuchen.'); return; }
      onCreated({ code: d.treeCode, name: d.treeName || name.trim(), role: 'owner', status: 'erstellt' });
    } catch { setError('Server nicht erreichbar.'); }
    finally { setBusy(false); }
  };

  const ownTrees = trees.filter(t => t.role === 'owner' || t.role === 'helper');
  const rowStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: 'none', border: 'none', width: '100%', textAlign: 'left' };

  return (
    <div className="ud-modal-overlay" onClick={onClose}>
      <div className="ud-modal" style={{ width: 'min(560px, 100%)' }} onClick={e => e.stopPropagation()}>
        <div className="ud-modal-head">
          <h3>Erweiterung erstellen</h3>
          <button className="ud-modal-close" onClick={onClose} aria-label="Schliessen">×</button>
        </div>

        {step === 'base' && (<>
          <p className="ud-modal-text">
            Mit einer Erweiterung kommt die Familie einer weiteren Person dazu – zum Beispiel die
            Familie des anderen Elternteils. Zu welchem Stammbaum gehört die Erweiterung?
          </p>
          <div className="ud-helper-list">
            {ownTrees.map(t => (
              <button key={t.code} style={rowStyle} disabled={busy} onClick={() => chooseBase(t)}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{t.status === 'erstellt' ? 'Entwurf' : 'Bestellt'} · {t.code}</div>
                </div>
                <span style={{ color: '#1540dc' }}>›</span>
              </button>
            ))}
          </div>
          {busy && <p className="ud-modal-text">Wird geladen…</p>}
        </>)}

        {step === 'anchor' && (<>
          <p className="ud-modal-text">
            Wessen Familie soll dazukommen? Wähle die Person, deren Eltern und Geschwister
            in der Erweiterung neu erfasst werden.
          </p>
          <div className="ud-helper-list" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {anchors.length === 0 && <div className="ud-helper-empty">In diesem Stammbaum haben alle Personen schon Eltern.</div>}
            {anchors.map(p => (
              <button key={p.id} style={rowStyle} onClick={() => chooseAnchor(p.id)}>
                <Avatar p={p} />
                <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{p.name || '(ohne Namen)'}</div>
                <span style={{ color: '#1540dc' }}>›</span>
              </button>
            ))}
          </div>
          <div className="ud-code-links"><button className="ud-link" onClick={() => setStep('base')}>← anderer Stammbaum</button></div>
        </>)}

        {step === 'select' && (<>
          <p className="ud-modal-text">
            Diese Personen werden aus „{baseTree.name}“ übernommen. Sie erscheinen grau, behalten ihre
            Farben und Symbole und können nur im Original bearbeitet werden. Beim Bestellen als
            Erweiterung werden ihre Karten nicht nochmals gedruckt.
          </p>
          <div className="ud-helper-list" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
            {(base.persons || []).map(p => (
              <label key={p.id} style={{ ...rowStyle, cursor: p.id === anchorId ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={selected.has(p.id)} disabled={p.id === anchorId} onChange={() => toggle(p.id)} />
                <Avatar p={p} />
                <div style={{ flex: 1, fontSize: 14 }}>
                  {p.name || '(ohne Namen)'}{p.id === anchorId && <strong style={{ color: '#1540dc' }}> · Ausgangsperson</strong>}
                </div>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
            <button className="ud-link" onClick={() => setStep('anchor')}>← andere Person</button>
            <button className="ud-btn ud-btn--primary" onClick={() => setStep('name')}>Weiter ({selected.size} Personen)</button>
          </div>
        </>)}

        {step === 'name' && (
          <form onSubmit={create}>
            <p className="ud-modal-text">Wie soll die Erweiterung heissen?</p>
            <input className="ud-input" style={{ width: '100%', padding: '9px 10px', border: '1px solid #ccc', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
              value={name} onChange={e => setName(e.target.value)} autoFocus />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
              <button type="button" className="ud-link" onClick={() => setStep('select')}>← zurück</button>
              <button className="ud-btn ud-btn--primary" type="submit" disabled={busy}>{busy ? 'Wird erstellt…' : 'Erweiterung erstellen'}</button>
            </div>
          </form>
        )}

        {error && <p className="ud-modal-text" style={{ color: '#c00' }}>{error}</p>}
      </div>
    </div>
  );
}
